import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/page";
import { Documents } from "@/components/documents/Documents";
import { useDisableTeam } from "@/components/page/disableTeam";
import { useMemo, useState } from "react";
import { RoleProtected } from "@/components/common/RoleProtected";
import { Button } from "@repo/ui/components/Base/Button";
import { useUserStore } from "@/stores/userStore";
import { AgendaItemForm } from "@/components/agendaItems/AgendaItemForm";
import { AgendaItemReviewDialog } from "@/components/agendaItems/AgendaItemReviewDialog";
import { useGetAgendaItem } from "@/hooks/queryHooks/agendaItemHooks";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";

export const AgendaItemPage = () => {
  const { tenantId, agendaItemId } = useParams();
  const navigate = useNavigate();
  const { activeTeam, user, hasMinRole } = useUserStore((s) => ({
    activeTeam: s.activeTeam,
    user: s.user,
    hasMinRole: s.hasMinRole,
  }));
  const activeTeamId =
    activeTeam && activeTeam !== "org" ? activeTeam.id : "org";

  useDisableTeam();

  const [formKey, setFormKey] = useState(0);
  const resetForm = () => setFormKey((key) => key + 1);

  const { data: tenantData } = useGetTenantByFriendly(tenantId ?? "");
  const { data: agendaItemData } = useGetAgendaItem(
    tenantData?.id ?? "",
    agendaItemId ?? ""
  );

  const teamMismatch = agendaItemData?.teamId != activeTeamId;

  const [agendaItemViewOnly, setAgendaItemViewOnly] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  type ReviewAllowed = boolean | "direct";

  const reviewAllowed: ReviewAllowed = useMemo(() => {
    // no approval items
    if (!agendaItemData?.approver) return false; // cannot record decisions

    // team-approved items
    if (agendaItemData.approver.approverType === "team") {
      if (activeTeam === "org" || !activeTeam) return false;
      // only allow if you are in team
      return agendaItemData.approver.approverId === activeTeam.id;
    }

    // user-approved items
    if (agendaItemData.approver.approverType === "user") {
      if (!user) return false;
      // only allow if you are the approving user
      return agendaItemData.approver.approverId === user.id ? "direct" : false;
    }

    // unknown state: don't allow
    return false;
  }, [activeTeam, user, agendaItemData]);

  if (!agendaItemData || !tenantData) return null;

  const showReviewButton =
    (!agendaItemData.review?.status ||
      agendaItemData.review.status === "pending" ||
      agendaItemData.review.status === "deferred") &&
    reviewAllowed &&
    hasMinRole(reviewAllowed === "direct" ? "view" : "edit");

  const handleEditAgendaItem = () => {
    setAgendaItemViewOnly(false);
  };

  const handleComplete = () => {
    setAgendaItemViewOnly(true);
  };

  const handleCancel = () => {
    setAgendaItemViewOnly(true);
    resetForm();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex flex-col flex-grow">
      <AgendaItemReviewDialog
        open={reviewDialogOpen}
        setOpen={setReviewDialogOpen}
        tenantId={tenantData.id}
        agendaItem={agendaItemData}
      />

      <PageHeader title={`${tenantData.name}: ${agendaItemData.name}`}>
        {" "}
      </PageHeader>
      <RoleProtected minRole="edit">
        <div className="flex flex-row justify-end pb-2">
          <Button
            tooltip={
              teamMismatch
                ? "You are in the wrong team to be editing this agenda item"
                : "Edit Agenda Item"
            }
            onClick={handleEditAgendaItem}
            disabled={teamMismatch || !agendaItemViewOnly}
          >
            Edit
          </Button>
        </div>
      </RoleProtected>
      {tenantData?.id && (
        <div className="flex flex-row gap-4 justify-center">
          <div className="w-0 flex-grow-[3] max-w-screen-md">
            <AgendaItemForm
              key={formKey}
              viewOnly={agendaItemViewOnly}
              tenantId={tenantData.id}
              agendaItemFormData={agendaItemData}
              handleCancel={handleCancel}
              handleComplete={handleComplete}
              handleGoBack={handleGoBack}
              showReviewButton={showReviewButton}
              reviewButtonDisabled={
                !agendaItemViewOnly ||
                (teamMismatch && reviewAllowed !== "direct")
              }
              onReviewButtonClick={() => setReviewDialogOpen(true)}
            />
          </div>
          <div className="w-0 flex-grow-[2] max-w-screen-md">
            <Documents agendaItem={agendaItemData} tenantId={tenantData.id} />
          </div>
        </div>
      )}
    </div>
  );
};
